import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { css, msg, fmtFecha, fmtFechaHora, hoyISO, CARGOS, TIPOS_EVAL, Cab, Campo, Pager, DocumentoLegal, FirmaPad, imprimirDocumento, imprimirConsentimiento } from './comun';
import FirmarAcuerdo from './FirmarAcuerdo';
import Femo from './Femo';
import Certificado from './Certificado';

const ESTADO = { BORRADOR: ['badge-amber', 'Borrador'], AUTORIZADA: ['badge-green', 'Autorizada'], EN_FEMO: ['badge-amber', 'FEMO en curso'], FEMO_LISTO: ['badge-green', 'FEMO completo'], CERTIFICADO: ['badge-green', 'Certificado emitido'], ANULADA: ['badge-red', 'Anulada'] };
const Estado = ({ e }) => { const [c, t] = ESTADO[e] || ['badge-gray', e]; return <span className={'badge ' + c}>{t}</span>; };
const PAGINA = 25;

/* ── nueva evaluación ──────────────────────────────────── */
function NuevaEvaluacion({ onVolver, onCreada }) {
  const [empresas, setEmpresas] = useState([]);
  const [empresa, setEmpresa] = useState('');
  const [q, setQ] = useState('');
  const [encontrados, setEncontrados] = useState([]);
  const [trab, setTrab] = useState(null);
  const [tipo, setTipo] = useState('INGRESO');
  const [yo, setYo] = useState(null);                  // si quien está en sesión es médico/enfermera, el profesional es él mismo
  const [motivo, setMotivo] = useState('');            // por qué el usuario en sesión no puede crear evaluaciones
  const [fecha, setFecha] = useState(hoyISO());
  const [falta, setFalta] = useState(null);            // { profesional, empresa } cuando falta el acuerdo
  const [guardando, setGuardando] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    Promise.all([api.get('/ocupacional/empresas'), api.get('/ocupacional/evaluaciones/atienden')])
      .then(([e, a]) => {
        setEmpresas(e.data); setYo(a.data.yo || null); setMotivo(a.data.motivo || '');
      })
      .catch(e => setErr(msg(e)));
  }, []);

  useEffect(() => {
    if (!empresa) { setEncontrados([]); return undefined; }
    const t = setTimeout(() => {
      const p = new URLSearchParams({ empresa, activo: 'todos', limit: '8' });
      if (q.trim()) p.set('q', q.trim());
      api.get(`/ocupacional/trabajadores?${p.toString()}`).then(r => setEncontrados(r.data.rows)).catch(e => setErr(msg(e)));
    }, 250);
    return () => clearTimeout(t);
  }, [empresa, q]);

  function elegir(t) {
    setTrab(t);
    if (!t.ACTIVO) setTipo('RETIRO');
  }

  async function crear() {
    setGuardando(true); setErr(''); setFalta(null);
    try {
      const r = await api.post('/ocupacional/evaluaciones', { trabajador: trab.COD, tipo, fecha_atencion: fecha });
      onCreada(r.data.COD);
    } catch (e) {
      const d = e && e.response && e.response.data;
      if (d && d.codigo === 'ACUERDO_PENDIENTE') setFalta({ profesional: d.profesional, nombre: d.nombre, empresa: d.empresa, texto: d.error });
      else setErr(msg(e));
    } finally { setGuardando(false); }
  }

  if (falta) {
    const p = { USUARIO: falta.profesional, NOMBRE: falta.nombre };
    return <FirmarAcuerdo profesional={p} empresaId={falta.empresa} onVolver={() => setFalta(null)} onHecho={() => { setFalta(null); crear(); }} />;
  }

  const listo = empresa && trab && yo && tipo && fecha && !guardando;
  return (
    <div className="mo-card fade-up">
      <Cab onVolver={onVolver} titulo="Nueva evaluación" sub="Encabezado de la evaluación médica ocupacional">
        <button className="btn btn-primary btn-sm" disabled={!listo} onClick={crear}>{guardando ? 'Guardando…' : '✓ Crear evaluación'}</button>
      </Cab>
      {err && <div className="mo-alert">{err}</div>}
      <div className="mo-form">
        <div className="mo-section">Trabajador</div>
        <Campo label="Empresa" req full>
          <select className="form-control" value={empresa} onChange={e => { setEmpresa(e.target.value); setTrab(null); setQ(''); }}>
            <option value="">— Elija la empresa —</option>
            {empresas.map(e => <option key={e.COD} value={e.COD}>{e.RAZON_SOCIAL}</option>)}
          </select>
        </Campo>
        {empresa && (
          <Campo label="Trabajador" req full>
            {trab ? (
              <div className="mo-lin">
                <div className="mo-dato"><b>{trab.APELLIDOS} {trab.NOMBRES}</b> · <span className="mo-mono">{trab.IDENTIFICACION}</span>{trab.PUESTO_NOMBRE ? ` · ${trab.PUESTO_NOMBRE}` : ''}{!trab.ACTIVO && <span className="badge badge-gray" style={{ marginLeft: 8 }}>Ya salió</span>}</div>
                <button type="button" className="btn btn-sm" onClick={() => setTrab(null)}>Cambiar</button>
              </div>
            ) : (
              <>
                <input className="form-control" placeholder="Buscar por cédula o apellido..." value={q} onChange={e => setQ(e.target.value)} />
                <div className="mo-sel-lista" style={{ marginTop: 6 }}>
                  {encontrados.length === 0 ? <div style={{ cursor: 'default', color: 'var(--text3)' }}>Sin resultados. Agregue al trabajador en la nómina de la empresa.</div>
                    : encontrados.map(t => (
                      <div key={t.COD} onClick={() => elegir(t)}>
                        <b>{t.APELLIDOS} {t.NOMBRES}</b> <span className="mo-mono">· {t.IDENTIFICACION}</span>{t.PUESTO_NOMBRE ? ` · ${t.PUESTO_NOMBRE}` : ''}{!t.ACTIVO ? ' · (ya salió)' : ''}
                      </div>))}
                </div>
              </>
            )}
          </Campo>
        )}
        <div className="mo-section">Evaluación</div>
        <Campo label="Tipo de evaluación" req>
          <select className="form-control" value={tipo} onChange={e => setTipo(e.target.value)}>
            {Object.entries(TIPOS_EVAL).map(([k, v]) => <option key={k} value={k} disabled={!!trab && !trab.ACTIVO && k !== 'RETIRO'}>{v}</option>)}
          </select>
        </Campo>
        <Campo label="Fecha de atención" req><input type="date" className="form-control" value={fecha} onChange={e => setFecha(e.target.value)} /></Campo>
        <Campo label="Profesional que atiende" req full>
          {yo
            ? <div className="mo-dato">{yo.NOMBRE} · {CARGOS[yo.CARGO] || yo.CARGO} <span className="mo-sub">(usted, con su usuario)</span></div>
            : <div className="mo-sub" style={{ color: '#b42318' }}>{motivo || 'Cargando…'}</div>}
        </Campo>
      </div>
    </div>
  );
}

/* ── detalle: acuerdo → autorización → FEMO ────────────── */
function DetalleEvaluacion({ id, onVolver }) {
  const [d, setD] = useState(null);
  const [vista, setVista] = useState('detalle');       // detalle | acuerdo | femo
  const [medio, setMedio] = useState('DIGITAL');
  const [firma, setFirma] = useState(null);
  const [anular, setAnular] = useState(null);          // 'acuerdo' | 'auth' | 'eval'
  const [motivo, setMotivo] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [err, setErr] = useState('');
  const cargar = useCallback(() => api.get(`/ocupacional/evaluaciones/${id}`).then(r => { setD(r.data); setErr(''); }).catch(e => setErr(msg(e))), [id]);
  useEffect(() => { cargar(); }, [cargar]);

  const trab = d && d.trabajador ? d.trabajador : {};
  const firmanteTrab = { nombre: `${trab.NOMBRES || ''} ${trab.APELLIDOS || ''}`.trim(), detalle: trab.IDENTIFICACION ? 'C.I. ' + trab.IDENTIFICACION : '' };
  const firmanteProf = { nombre: d ? d.PROF_NOMBRE : '', detalle: d && d.PROF_CARGO ? (CARGOS[d.PROF_CARGO] || d.PROF_CARGO) : '' };
  const imprimirAcuerdo = () => imprimirConsentimiento(api, d.acuerdo.COD, { firmante: firmanteProf, firma_label: 'FIRMA DEL SERVIDOR:', archivo: `Acuerdo de confidencialidad - ${d.PROF_NOMBRE}` }).catch(e => setErr(msg(e)));
  const imprimirAuth = () => imprimirConsentimiento(api, d.autorizacion.COD, { firmante: firmanteTrab, firma_label: 'FIRMA DEL TRABAJADOR:', archivo: `Autorizacion - ${firmanteTrab.nombre}` }).catch(e => setErr(msg(e)));
  const imprimirEnBlanco = () => imprimirDocumento({ titulo: d.documento.titulo, parrafos: d.documento.parrafos, firma_label: d.documento.firma_label, firmante: firmanteTrab, archivo: `Autorizacion - ${firmanteTrab.nombre}`, pie: [`${d.documento.version} · para firmar en papel`] });

  async function firmar() {
    setGuardando(true); setErr('');
    try {
      await api.post(`/ocupacional/evaluaciones/${id}/autorizacion`, medio === 'PAPEL' ? { medio } : { medio, firma });
      setFirma(null); await cargar();
    } catch (e) { setErr(msg(e)); } finally { setGuardando(false); }
  }
  async function confirmarAnulacion() {
    try {
      if (anular === 'acuerdo') await api.post(`/ocupacional/consentimientos/${d.acuerdo.COD}/anular`, { motivo });
      else if (anular === 'auth') await api.post(`/ocupacional/consentimientos/${d.autorizacion.COD}/anular`, { motivo });
      else await api.post(`/ocupacional/evaluaciones/${id}/anular`, { motivo });
      setAnular(null); setMotivo(''); await cargar();
    } catch (e) { setErr(msg(e)); }
  }

  if (!d) return <div className="mo-card"><Cab onVolver={onVolver} titulo="Evaluación" />{err ? <div className="mo-alert">{err}</div> : <div style={{ padding: 40 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>}</div>;
  if (vista === 'femo') return <Femo id={id} onVolver={() => { setVista('detalle'); cargar(); }} />;
  if (vista === 'acuerdo') {
    return <FirmarAcuerdo profesional={{ USUARIO: d.PROFESIONAL, NOMBRE: d.PROF_NOMBRE }} empresaId={d.COD_EMPRESA}
      onVolver={() => setVista('detalle')} onHecho={() => { setVista('detalle'); cargar(); }} />;
  }

  const anulada = d.ESTADO === 'ANULADA';
  const t = d.trabajador || {};
  const paso = (n, ok, act, texto) => <div className={'mo-paso' + (ok ? ' ok' : act ? ' act' : '')}><span className="n">{ok ? '✓' : n}</span>{texto}</div>;
  const puedeFirmar = !guardando && (medio === 'PAPEL' || !!firma);

  return (
    <div className="mo-card fade-up">
      <Cab onVolver={onVolver} titulo={`Evaluación de ${(TIPOS_EVAL[d.TIPO] || d.TIPO).toLowerCase()}`} sub={`${d.APELLIDOS} ${d.NOMBRES} · ${d.RAZON_SOCIAL}`}>
        <Estado e={d.ESTADO} />
        {!anulada && <button className="btn btn-sm" onClick={() => { setAnular('eval'); setMotivo(''); }}>Anular evaluación</button>}
      </Cab>
      {err && <div className="mo-alert">{err}</div>}
      <div className="mo-form">
        <div><div className="form-label">Trabajador</div><div className="mo-dato">{d.APELLIDOS} {d.NOMBRES}<div className="mo-sub mo-mono">{d.IDENTIFICACION}</div></div></div>
        <div><div className="form-label">Puesto</div><div className="mo-dato">{d.PUESTO || '—'}</div></div>
        <div><div className="form-label">Fecha de atención</div><div className="mo-dato">{fmtFecha(d.FECHA_ATENCION)}</div></div>
        <div><div className="form-label">Profesional</div><div className="mo-dato">{d.PROF_NOMBRE}{d.PROF_CARGO ? ` · ${CARGOS[d.PROF_CARGO] || d.PROF_CARGO}` : ''}</div></div>
      </div>
      {anular && (
        <div className="mo-panel">
          <span>{anular === 'acuerdo' ? `Anular el acuerdo de confidencialidad de ${d.PROF_NOMBRE} con esta empresa. Quedará registrado; para seguir evaluando allí deberá firmarse uno nuevo.`
            : anular === 'auth' ? 'Anular la autorización firmada: la evaluación vuelve a borrador y habrá que firmar otra.' : 'Anular toda la evaluación (y su autorización). Queda registrada, no se borra.'}</span>
          <input className="form-control" style={{ maxWidth: 280 }} placeholder="Motivo (mínimo 5 letras)" value={motivo} onChange={e => setMotivo(e.target.value)} />
          <button className="btn btn-sm btn-primary" disabled={motivo.trim().length < 5} onClick={confirmarAnulacion}>Anular</button>
          <button className="btn btn-sm" onClick={() => { setAnular(null); setMotivo(''); }}>Cancelar</button>
        </div>
      )}
      <div className="mo-pasos" style={{ marginTop: 14, borderTop: '1px solid var(--border)' }}>
        {paso(1, !!d.acuerdo, !d.acuerdo && !anulada, 'Acuerdo de confidencialidad')}
        {paso(2, !!d.autorizacion, !!d.acuerdo && !d.autorizacion && !anulada, 'Autorización del trabajador')}
        {paso(3, ['FEMO_LISTO', 'CERTIFICADO'].includes(d.ESTADO), ['AUTORIZADA', 'EN_FEMO'].includes(d.ESTADO), 'Formulario FEMO')}
        {paso(4, d.ESTADO === 'CERTIFICADO', d.ESTADO === 'FEMO_LISTO', 'Certificado')}
      </div>

      {/* 1 · acuerdo */}
      {!anulada && !d.acuerdo && (
        <div className="mo-cuerpo">
          <div className="mo-warn" style={{ margin: 0 }}>
            El acuerdo de confidencialidad de {d.PROF_NOMBRE} con esta empresa no está vigente. Debe firmarse antes de continuar.
            {' '}<button className="btn btn-sm btn-primary" onClick={() => setVista('acuerdo')}>Firmar acuerdo</button>
          </div>
        </div>
      )}
      {d.acuerdo && (
        <div className="mo-cuerpo"><div className="mo-sub">Acuerdo de confidencialidad vigente desde el {fmtFechaHora(d.acuerdo.FECHA_FIRMA)} ({d.acuerdo.MEDIO === 'PAPEL' ? 'en papel' : 'firma digital'}).
          <> <button className="btn btn-sm" onClick={imprimirAcuerdo}>🖨 Imprimir / PDF</button></>
          {!anulada && <> <button className="btn btn-sm" onClick={() => { setAnular('acuerdo'); setMotivo(''); }}>Anular acuerdo</button></>}</div></div>
      )}

      {/* 2 · autorización */}
      {!anulada && d.acuerdo && !d.autorizacion && d.documento && (
        <>
          <div className="mo-cuerpo"><div className="mo-note">Lea el texto al trabajador (o entrégueselo) y pídale que firme. Sin esta autorización no se puede llenar el FEMO.</div></div>
          <DocumentoLegal documento={d.documento} />
          <div className="mo-cuerpo" style={{ paddingBottom: 0 }}><button className="btn btn-sm" onClick={imprimirEnBlanco}>🖨 Imprimir para firmar en papel / PDF</button></div>
          <div className="mo-cuerpo">
            <div className="mo-lin">
              <label className="mo-chk"><input type="radio" name="medio" checked={medio === 'DIGITAL'} onChange={() => setMedio('DIGITAL')} /> Firma digital del trabajador</label>
              <label className="mo-chk"><input type="radio" name="medio" checked={medio === 'PAPEL'} onChange={() => setMedio('PAPEL')} /> Firmó en papel (solo se registra)</label>
            </div>
            {medio === 'DIGITAL' && <FirmaPad onChange={setFirma} />}
            <div><button className="btn btn-primary" disabled={!puedeFirmar} onClick={firmar}>{guardando ? 'Guardando…' : '✓ Firmar autorización'}</button></div>
          </div>
        </>
      )}
      {d.autorizacion && (
        <div className="mo-cuerpo">
          <div className="mo-ok" style={{ margin: 0 }}>
            Autorización firmada por {t.NOMBRES} {t.APELLIDOS} el {fmtFechaHora(d.autorizacion.FECHA_FIRMA)} ({d.autorizacion.MEDIO === 'PAPEL' ? 'en papel' : 'firma digital'}).
            <> <button className="btn btn-sm" onClick={imprimirAuth}>🖨 Imprimir / PDF</button></>
            {!anulada && <> <button className="btn btn-sm" onClick={() => { setAnular('auth'); setMotivo(''); }}>Anular autorización</button></>}
          </div>
          {d.autorizacion.FIRMA && <img className="mo-sig-img" src={d.autorizacion.FIRMA} alt="Firma del trabajador" />}
          <div className="mo-hash">{d.autorizacion.VERSION_TEXTO} · huella SHA-256: {d.autorizacion.HASH_TEXTO}</div>
          {!anulada && (
            <div className="mo-cuerpo" style={{ padding: 0 }}>
              {d.es_profesional
                ? <button className="btn btn-primary" onClick={() => setVista('femo')}>{['FEMO_LISTO', 'CERTIFICADO'].includes(d.ESTADO) ? 'Ver el FEMO' : d.ESTADO === 'EN_FEMO' ? 'Continuar el FEMO' : 'Llenar el FEMO'}</button>
                : <div className="mo-note">El formulario FEMO contiene datos clínicos: solo lo abre el profesional que atiende ({d.PROF_NOMBRE}), con su propio usuario.</div>}
            </div>)}
        </div>
      )}
      {!anulada && ['FEMO_LISTO', 'CERTIFICADO'].includes(d.ESTADO) && <Certificado id={id} estado={d.ESTADO} onCambio={cargar} />}
      {d.autorizaciones_anuladas && d.autorizaciones_anuladas.length > 0 && (
        <div className="mo-cuerpo">
          <div className="form-label">Autorizaciones anuladas</div>
          {d.autorizaciones_anuladas.map(a => <div key={a.COD} className="mo-sub">Firmada el {fmtFechaHora(a.FECHA_FIRMA)}, anulada el {fmtFechaHora(a.FECHA_ANULACION)}: {a.MOTIVO_ANULACION}</div>)}
        </div>
      )}
    </div>
  );
}

/* ── lista ─────────────────────────────────────────────── */
function ListaEvaluaciones({ onAbrir, onNueva }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [empresas, setEmpresas] = useState([]);
  const [empresa, setEmpresa] = useState('');
  const [estado, setEstado] = useState('');
  const [q, setQ] = useState('');
  const [pag, setPag] = useState(0);
  const [cargando, setCargando] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => { api.get('/ocupacional/empresas').then(r => setEmpresas(r.data)).catch(() => {}); }, []);
  useEffect(() => { setPag(0); }, [empresa, estado, q]);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const p = new URLSearchParams({ limit: String(PAGINA), offset: String(pag * PAGINA) });
      if (empresa) p.set('empresa', empresa);
      if (estado) p.set('estado', estado);
      if (q.trim()) p.set('q', q.trim());
      const r = await api.get(`/ocupacional/evaluaciones?${p.toString()}`);
      setRows(r.data.rows); setTotal(r.data.total); setErr('');
    } catch (e) { setErr(msg(e)); } finally { setCargando(false); }
  }, [empresa, estado, q, pag]);
  useEffect(() => { const t = setTimeout(cargar, 250); return () => clearTimeout(t); }, [cargar]);

  const paginas = Math.max(1, Math.ceil(total / PAGINA));
  return (
    <div className="mo-card fade-up">
      <div className="mo-head">
        <span className="mo-title">Evaluaciones</span>
        <button className="btn btn-sm" style={{ background: 'var(--navy)', color: '#fff', border: 'none', fontWeight: 700 }} onClick={onNueva}>+ Nueva evaluación</button>
      </div>
      <div className="mo-toolbar">
        <div className="search-wrap" style={{ flex: '1 1 220px', maxWidth: 360, position: 'relative' }}>
          <span className="search-icon" style={{ fontSize: 13 }}>⊙</span>
          <input className="form-control" placeholder="Buscar trabajador por nombre o cédula..." value={q} onChange={e => setQ(e.target.value)} />
        </div>
        <select className="form-control" style={{ maxWidth: 240 }} value={empresa} onChange={e => setEmpresa(e.target.value)}>
          <option value="">Todas las empresas</option>
          {empresas.map(e => <option key={e.COD} value={e.COD}>{e.RAZON_SOCIAL}</option>)}
        </select>
        <select className="form-control" style={{ maxWidth: 170 }} value={estado} onChange={e => setEstado(e.target.value)}>
          <option value="">Todos los estados</option>
          <option value="BORRADOR">Borrador</option><option value="AUTORIZADA">Autorizada</option><option value="EN_FEMO">FEMO en curso</option><option value="FEMO_LISTO">FEMO completo</option><option value="CERTIFICADO">Certificado emitido</option><option value="ANULADA">Anulada</option>
        </select>
      </div>
      {err && <div className="mo-alert" style={{ marginBottom: 14 }}>{err}</div>}
      <div className="table-wrap">
        <table className="mo-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr><th>Fecha</th><th>Trabajador</th><th className="hide-mobile">Empresa</th><th>Tipo</th><th className="hide-mobile">Profesional</th><th>Estado</th></tr></thead>
          <tbody>
            {cargando && rows.length === 0 ? <tr><td colSpan={6} style={{ textAlign: 'center', padding: '32px 0' }}><div className="spinner" style={{ margin: '0 auto' }} /></td></tr>
              : rows.length === 0 ? <tr><td colSpan={6}><div className="empty-state">No hay evaluaciones. Cree la primera con «+ Nueva evaluación».</div></td></tr>
              : rows.map(r => (
                <tr key={r.COD} className="click" onClick={() => onAbrir(r.COD)}>
                  <td>{fmtFecha(r.FECHA_ATENCION)}</td>
                  <td><div style={{ fontWeight: 600, color: 'var(--navy)' }}>{r.APELLIDOS} {r.NOMBRES}</div><div className="mo-sub mo-mono">{r.IDENTIFICACION}</div></td>
                  <td className="hide-mobile">{r.RAZON_SOCIAL}</td>
                  <td>{TIPOS_EVAL[r.TIPO] || r.TIPO}</td>
                  <td className="hide-mobile">{r.PROF_NOMBRE}</td>
                  <td><Estado e={r.ESTADO} /></td>
                </tr>))}
          </tbody>
        </table>
      </div>
      <Pager pag={pag} paginas={paginas} total={total} pagina={PAGINA} setPag={setPag} />
    </div>
  );
}

/* ═════════════════ PÁGINA ═════════════════ */
export default function Evaluaciones() {
  const [vista, setVista] = useState({ t: 'lista' });   // lista | nueva | detalle
  return (
    <>
      <style>{css}</style>
      <div className="fade-up">
        {vista.t === 'lista' && <ListaEvaluaciones onAbrir={id => setVista({ t: 'detalle', id })} onNueva={() => setVista({ t: 'nueva' })} />}
        {vista.t === 'nueva' && <NuevaEvaluacion onVolver={() => setVista({ t: 'lista' })} onCreada={id => setVista({ t: 'detalle', id })} />}
        {vista.t === 'detalle' && <DetalleEvaluacion id={vista.id} onVolver={() => setVista({ t: 'lista' })} />}
      </div>
    </>
  );
}
